import { Platform } from "react-native";
import { Country, LOCALE, Locale } from "@lookiero/sty-psp-locale";
import { Tradename } from "@lookiero/sty-sp-tradename";

interface DevSettings {
  readonly tradename: Tradename;
  readonly locale: Locale;
  readonly country: Country;
}

const DEFAULT_DEV_SETTINGS: DevSettings = {
  tradename: Tradename.LOOKIERO,
  locale: Locale.es_ES,
  country: Country.ES,
};

const isOneOf = <T extends string>(values: readonly T[], value: string | undefined): value is T =>
  (values as readonly string[]).includes(value ?? "");

/**
 * Pure parsing, no gate: given a query string, resolves tradename/locale and derives country
 * from locale via packages/locale's LOCALE table (country is never read as its own param, so it
 * can't drift out of sync with the locale). Falls back to Lookiero/es-ES for missing or unknown
 * values, same contract as apps/style-profile/src/devSettings.ts.
 */
const devSettingsFromSearch = (search: URLSearchParams): DevSettings => {
  const tradename = search.get("tradename")?.toUpperCase();
  const locale = search.get("locale")?.replace("_", "-");
  const resolvedLocale = isOneOf(Object.values(Locale), locale) ? locale : Locale.es_ES;

  return {
    tradename: isOneOf(Object.values(Tradename), tradename) ? tradename : Tradename.LOOKIERO,
    locale: resolvedLocale,
    country: LOCALE[resolvedLocale][1],
  };
};

/**
 * Test-only override seam: tradename/locale/country can be forced via query string
 * (?tradename=&locale=), but ONLY when EXPO_PUBLIC_CI is set - the Playwright webServer sets it
 * for the dev server it boots (see playwright.config.ts), the same flag the apiUrl branch in
 * ExpoRoot.tsx already relies on. Consumers never reach this seam: index.ts exposes bootstrap/root
 * and nothing in that import graph pulls in ExpoRoot or this module, so it lands in no consumer
 * bundle. The EXPO_PUBLIC_CI check is the second lock, for this app's standalone Expo web build
 * (`sty-psp serve` -> `expo export`), where babel-preset-expo inlines EXPO_PUBLIC_* at build time
 * and the branch folds to a literal `false`. The query string is deliberately not parsed at all
 * when the gate is closed.
 */
const devSettingsForWeb = (): DevSettings =>
  Platform.OS === "web" && typeof window !== "undefined" && Boolean(process.env.EXPO_PUBLIC_CI)
    ? devSettingsFromSearch(new URLSearchParams(window.location.search))
    : DEFAULT_DEV_SETTINGS;

export { devSettingsForWeb, devSettingsFromSearch };
export type { DevSettings };
